import {ItecColFilters, ItecDataGrid, ItecGridColsDef as GridColsDef, useItecDataGrid} from 'src/lib/itaptec/datagrid/src'
import {GroupDTOFields, GroupFilterDTOFields, GroupViewDTO, GroupViewDTOFields} from 'src/types/group/groupData'
import {HttpGroup} from 'src/http/group/httpGroup'
import {EntityWithIdFields} from 'src/lib/itaptec/http_client/src'
import {Box, Card, CardHeader, Chip, Dialog, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip} from '@mui/material'
import CachedIcon from '@mui/icons-material/Cached'
import {FilterAltOff, Search} from '@mui/icons-material'
import {useConfirmationDialog} from 'src/hooks/useConfirmationDialog'
import toast from 'react-hot-toast'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import EditIcon from '@mui/icons-material/Edit'
import {GridActionsCellItem} from '@mui/x-data-grid'
import {useEffect, useState} from 'react'
import EditSectorComercialesGrupo from './components/EditSectorComercialesGrupo'
import GroupFormDialog from './components/GroupFormDialog'
import {z} from 'zod'
import {EntityWithIdAndDescriptionFields, PaisLogoFields, schemaSelectionOption} from 'src/types'
import DomainIcon from '@mui/icons-material/Domain'
import PeopleIcon from '@mui/icons-material/People'
import {WrapperTable} from '../components'
import {CountryAvatar} from "../../../../../components/icons/CountryAvatar"
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { Controller } from 'react-hook-form'
import EditGroupUsers from './components/EditGroupUsers'

const opcionActivo = {value: 1, label: "Activos", color: "success"}
const opcionTodos = {value: 2, label: "Todos", color: "info"}

const toggleActivoOptions: any[] = [
  opcionActivo,
  opcionTodos,
]

const schemaFiltroGrupos = z.object({
  [GroupFilterDTOFields.ActiveOnly]: z.array(schemaSelectionOption).optional(),
  [GroupFilterDTOFields.Name]: z.string().optional(),
  [GroupFilterDTOFields.CodsPais]: z.array(schemaSelectionOption).optional(),
  [GroupFilterDTOFields.CodsTipoMensaje]: z.array(schemaSelectionOption).optional()
})

type SchemaFiltroGruposTipo = z.infer<typeof schemaFiltroGrupos>

function TableGroups() {
  const {askConfirmation} = useConfirmationDialog()
  const {paisLogos} = usePaisLogo()

  const columnas: GridColsDef<GroupViewDTO>[] = [
    {
      headerName: 'Grupo',
      field: GroupDTOFields.Name,
      flex: 3
    },
    {
      headerName: 'Perfil',
      field: GroupViewDTOFields.ProfileName,
      flex: 2
    },
    {
      headerName: 'Pais',
      field: 'pais',
      flex: 2,
      renderCell: ({row: group}) => (
        <Chip
          label={group[GroupViewDTOFields.CountryName]}
          size='small'
          variant='outlined'
          avatar={
            <CountryAvatar countryCode={group[GroupViewDTOFields.CodPais]}/>
          }
        />
      )
    },
    {
      headerName: 'Sistema',
      field: GroupViewDTOFields.NombreSistema,
      flex: 3
    },
    {
      headerName: 'Activo',
      field: GroupViewDTOFields.Active,
      type: 'boolean',
      flex: 1,
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({row}) => getActions(row, true)
    }
  ]

  // Filtros internos para la reactividad del query (no se renderizan en headers)
  const filtros: ItecColFilters<SchemaFiltroGruposTipo> = {
    [GroupDTOFields.Name]: {
      type: "text",
      name: GroupFilterDTOFields.Name,
      props: { debounceTime: 500, size: "small" },
    },
    pais: {
      type: 'multiselect',
      name: GroupFilterDTOFields.CodsPais,
      props: { getOptions: () => Promise.resolve([]), size: 'small' }
    },
    [GroupViewDTOFields.NombreSistema]: {
      type: 'multiselect',
      name: GroupFilterDTOFields.CodsTipoMensaje,
      props: { getOptions: () => Promise.resolve([]), size: 'small' }
    },
    [GroupViewDTOFields.Active]: {
      type: "multiple-button-group",
      name: GroupFilterDTOFields.ActiveOnly,
      props: { options: toggleActivoOptions, enforceValueSet: false, size: "small" },
    },
  }

  const getActions = (group: GroupViewDTO, small: boolean) => [
    group[GroupViewDTOFields.Active] ? (
      <GridActionsCellItem
        onClick={() =>
          askConfirmation(() => {
            const promise = HttpGroup.deactivateGroup(group.id)

            toast
              .promise(promise, {
                success: 'Inactivado',
                error: 'No se pudo inactivar',
                loading: 'Inactivando'
              })
              .then(() => refetch())
          })
        }
        icon={
          <Tooltip title='Inactivar'>
            <PersonRemoveIcon color='error'/>
          </Tooltip>
        }
        label='Inactivar'
        showInMenu={small}
      />
    ) : (
      <GridActionsCellItem
        onClick={() =>
          askConfirmation(() => {
            const promise = HttpGroup.activateGroup(group.id)

            toast
              .promise(promise, {
                success: 'Activado',
                error: 'No se pudo activar',
                loading: 'Activando'
              })
              .then(() => refetch())
          })
        }
        icon={
          <Tooltip title='Activar'>
            <PersonAddIcon color='success'/>
          </Tooltip>
        }
        label='Activar'
        showInMenu={small}
      />
    ),
    <GridActionsCellItem
      icon={
        <Tooltip title='Editar'>
          <EditIcon color='info'/>
        </Tooltip>
      }
      label='Editar'
      showInMenu={small}
      onClick={() => {
        setSelectedGroup(group)
        setShowFormDialog(true)
      }}
    />,
    <GridActionsCellItem
      icon={
        <Tooltip title='Sectores comerciales'>
          <DomainIcon color='info'/>
        </Tooltip>
      }
      label='Sectores comerciales'
      showInMenu={small}
      onClick={() => {
        setGrupoSectores(group[EntityWithIdFields.Id])
      }}
    />,
    <GridActionsCellItem
      icon={
        <Tooltip title='Usuarios'>
          <PeopleIcon color='primary'/>
        </Tooltip>
      }
      label='Usuarios'
      showInMenu={small}
      onClick={() => {
        setSelectedGroupUsers(group)
      }}
    />
  ]

  const opcionUsuarioActivo = (filter: SchemaFiltroGruposTipo, field: keyof SchemaFiltroGruposTipo) => {
    const fieldValue = filter?.[field];

    if (Array.isArray(fieldValue) && fieldValue.length === 1) {
      if (fieldValue[0].value === opcionActivo.value) return true
      if (fieldValue[0].value === opcionTodos.value) return undefined
    }
    return undefined
  }

  const {
    datagridProps,
    control: {
      query: {refetch},
      form: filterForm
    }
  } = useItecDataGrid({
    pagination: true,
    columns: columnas,
    getEntitiesPaginated: (pagination, filter) =>
      HttpGroup.getGroupList(
        pagination,
        filter
          ? {
            nombre: filter[GroupFilterDTOFields.Name],
            codsPais: filter[GroupFilterDTOFields.CodsPais]?.map(c => (c as any).id ?? c.value),
            codsTipoMensaje: filter[GroupFilterDTOFields.CodsTipoMensaje]?.map(c => (c as any).id ?? c.value),
            [GroupFilterDTOFields.ActiveOnly]: opcionUsuarioActivo(filter, GroupFilterDTOFields.ActiveOnly),
          }
          : {}
      ),
    density: 'compact',
    filters: filtros
  })

  // Default: mostrar solo activos
  useEffect(() => {
    if (filterForm) {
      filterForm.setValue(GroupFilterDTOFields.ActiveOnly, [opcionActivo] as any)
    }
  }, [])

  // Sobreescribir las columnas para quitar los menus de filtro de los headers
  const cleanDatagridProps = {
    ...datagridProps,
    columns: datagridProps.columns.map(col => ({
      ...col,
      disableColumnMenu: true,
      renderHeader: undefined,
    })),
    slots: undefined
  }

  const handleClearFilters = () => {
    if (!filterForm) return
    filterForm.reset({
      [GroupFilterDTOFields.Name]: undefined,
      [GroupFilterDTOFields.CodsPais]: undefined,
      [GroupFilterDTOFields.CodsTipoMensaje]: undefined,
      [GroupFilterDTOFields.ActiveOnly]: [opcionActivo] as any
    })
  }

  const [showFormDialog, setShowFormDialog] = useState<boolean>(false)
  const [selectedGroup, setSelectedGroup] = useState<GroupViewDTO>()
  const [grupoSectores, setGrupoSectores] = useState<number>()
  const [selectedGroupUsers, setSelectedGroupUsers] = useState<GroupViewDTO>()

  return (
    <WrapperTable>
      <Card>
        <CardHeader
          title='Administración de grupos'
          action={
            <IconButton disabled={datagridProps.loading} onClick={() => refetch()}>
              <CachedIcon/>
            </IconButton>
          }
        />

        {/* Barra de filtros */}
        {filterForm && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mx: 2,
              mb: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover'
            }}
          >
            <Controller
              control={filterForm.control}
              name={GroupFilterDTOFields.Name}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label='Grupo'
                  size='small'
                  sx={{ minWidth: 180, flex: 1 }}
                  InputProps={{
                    startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                  }}
                />
              )}
            />
            <Box sx={{ minWidth: 180, flex: 1 }}>
              <ControlledAutocomplete
                control={filterForm.control}
                name={GroupFilterDTOFields.CodsPais}
                label='País'
                                                loadOptions={() =>
                  Promise.resolve(paisLogos).then(res =>
                    res.map(s => ({
                      id: s.id,
                      descripcion: s[EntityWithIdAndDescriptionFields.Description]
                    }))
                  )
                }
                multiple
              />
            </Box>
            <Box sx={{ minWidth: 180, flex: 1 }}>
              <ControlledAutocomplete
                control={filterForm.control}
                name={GroupFilterDTOFields.CodsTipoMensaje}
                label='Sistema'
                                                loadOptions={() =>
                  HttpCacheWorkflow.getSistemasTipos().then(res =>
                    res.map(s => ({
                      id: s.id,
                      descripcion: s.descripcion
                    }))
                  )
                }
                multiple
              />
            </Box>
            <Controller
              control={filterForm.control}
              name={GroupFilterDTOFields.ActiveOnly}
              render={({ field }) => (
                <ToggleButtonGroup
                  size='small'
                  exclusive
                  value={(field.value ?? [])[0]?.value ?? null}
                  onChange={(_, newValue: number | null) => {
                    if (newValue === null) return
                    const option = toggleActivoOptions.find(o => o.value === newValue)
                    field.onChange(option ? [option] : [])
                  }}
                >
                  {toggleActivoOptions.map(option => (
                    <ToggleButton key={option.value} value={option.value} color={option.color}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            />
            <Tooltip title='Limpiar filtros'>
              <IconButton size='small' onClick={handleClearFilters}>
                <FilterAltOff fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <ItecDataGrid {...cleanDatagridProps} />
      </Card>
      <>
        <GroupFormDialog
          open={showFormDialog}
          onClose={() => setShowFormDialog(false)}
          group={selectedGroup}
          maxWidth={'md'}
          onAfterSubmit={() => {
            refetch()
            setSelectedGroup(undefined)
            setShowFormDialog(false)
          }}
          onSubmitError={(e: string) => console.log(e)}
        />
        <Dialog open={!!grupoSectores} fullWidth maxWidth='lg' keepMounted={false} sx={{maxHeight: '100%'}}>
          {grupoSectores && (
            <EditSectorComercialesGrupo
              onClose={() => {
                setGrupoSectores(undefined)
              }}
              grupo={grupoSectores}
            />
          )}
        </Dialog>
        <Dialog open={!!selectedGroupUsers} fullWidth maxWidth='lg' keepMounted={false}>
          {selectedGroupUsers && (
            <EditGroupUsers
              onClose={() => {
                setSelectedGroupUsers(undefined)
                refetch()
              }}
              group={selectedGroupUsers}
            />
          )}
        </Dialog>
      </>
    </WrapperTable>
  )
}

export default TableGroups
